import { DefaultLogger, SafeShell, type LogLevel, type Tracker } from '../../../core';
import { createAutoPageView } from './create-auto-page-view';
import { registerPageViewController } from './controller';
import { subscribeAppVisibility } from './app-visibility';
import type { AppLifecycleHost, AutoPageViewConfig, PageViewAdapter } from './types';

/** 工厂初始化最后一步安装采集器，保证同步首屏事件已有 Context 和系统插件。 */
export function wireAutoPageView(tracker: Tracker, enabled: boolean | AutoPageViewConfig | undefined,
  adapter: PageViewAdapter | undefined, logLevel?: LogLevel, appLifecycle?: AppLifecycleHost): void {
  const log = new DefaultLogger(logLevel);
  const safe = new SafeShell(log);
  if (!enabled || !adapter) {
    if (Boolean(enabled) !== Boolean(adapter)) safe.run(() => log.warn('[AutoPageView] 开关与适配器必须同时提供，自动 PV 未启用'), undefined);
    return;
  }
  const config = typeof enabled === 'object' ? { ...enabled } : {};
  const generated = new WeakSet<object>();
  const collector = createAutoPageView({ ...config, logger: log, track(name, properties) {
    generated.add(properties);
    try { tracker.track(name, properties); } finally { generated.delete(properties); }
  } });
  let active = false;
  let disposed = false;
  let unregister = () => {};
  let unsubscribe = () => {};
  const dispose = () => {
    if (disposed) return;
    disposed = true;
    active = false;
    unregister();
    unsubscribe();
    safe.run(() => adapter.uninstall?.(collector), undefined, 'PageView.uninstall');
    collector.destroy();
  };
  tracker.use({
    name: 'hss-auto-page-view-plugin',
    init() {
      const installed = safe.run(() => adapter.install(collector) !== false, false, 'PageView.install');
      if (!installed) { dispose(); return; }
      active = true;
      unsubscribe = subscribeAppVisibility(appLifecycle, collector, safe, log);
      unregister = registerPageViewController(tracker, {
        ready: () => collector.ready(),
        bridgeManualPageView: page => collector.bridgeManualPageView(page),
      });
    },
    beforeProduce(name, properties) {
      if (!active || generated.has(properties) || !config.legacyPageView) return;
      // 只有宿主明确识别的旧 PV 才应用归属规则；异常/未知结果保留旧事件。
      return safe.run(() => {
        const path = config.legacyPageView!(name, properties);
        if (typeof path !== 'string' || !path.trim()) return true;
        return collector.allowsManual(path);
      }, true, 'PageView.legacyPageView');
    },
    destroy: dispose,
  });
}
